import { __assign } from "tslib";
import React, { useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Tooltip, Text } from '@keen.io/ui-core';
import { useTooltip } from '@keen.io/react-hooks';
import { formatValue as valueFormatter, } from '@keen.io/charts-utils';
import GaugeProgress from './gauge-progress.component';
import GaugeLabels from './gauge-labels.component';
import { generateGauge } from './utils';
import { ChartBase } from '../../components';
import { theme as defaultTheme } from '../../theme';
import { TOOLTIP_MOTION, TOOLTIP_TIMEOUT } from '../../constants';
var createArcMotion = function (index) { return ({
    initial: { opacity: 0 },
    animate: { opacity: 0.5 },
    exit: {},
    transition: { delay: 0.005 * index, duration: 0 },
}); };
var progressValueMotion = {
    transition: { duration: 0.3 },
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
};
export var GaugeChart = function (_a) {
    var data = _a.data, valueKey = _a.valueKey, svgDimensions = _a.svgDimensions, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, _c = _a.startAngle, startAngle = _c === void 0 ? -120 : _c, _d = _a.endAngle, endAngle = _d === void 0 ? 120 : _d, _e = _a.margins, margins = _e === void 0 ? { top: 10, right: 0, bottom: 10, left: 0 } : _e, _f = _a.colorMode, colorMode = _f === void 0 ? 'continuous' : _f, _g = _a.colorSteps, colorSteps = _g === void 0 ? 2 : _g, _h = _a.progressType, progressType = _h === void 0 ? 'normal' : _h, _j = _a.minValue, minValue = _j === void 0 ? 'auto' : _j, _k = _a.maxValue, maxValue = _k === void 0 ? 'auto' : _k, formatValue = _a.formatValue, formatTooltip = _a.formatTooltip;
    var _l = generateGauge({
        data: data,
        dimension: svgDimensions,
        valueKey: valueKey,
        margins: margins,
        minValue: minValue,
        maxValue: maxValue,
        startAngle: startAngle,
        endAngle: endAngle,
        colorSteps: colorSteps,
        colorMode: colorMode,
        colors: theme.colors,
    }), progressValue = _l.progressValue, outerArcPath = _l.outerArcPath, emptySpaceArcPath = _l.emptySpaceArcPath, minimum = _l.minimum, maximum = _l.maximum, innerArcs = _l.innerArcs;
    var svgElement = useRef(null);
    var tooltipTimeout = useRef(null);
    var _m = useTooltip(svgElement), tooltipVisible = _m.tooltipVisible, tooltipPosition = _m.tooltipPosition, tooltipMeta = _m.tooltipMeta, updateTooltipPosition = _m.updateTooltipPosition, hideTooltip = _m.hideTooltip;
    var gaugeSettings = theme.gauge, tooltipSettings = theme.tooltip;
    return (React.createElement(React.Fragment, null,
        React.createElement(AnimatePresence, null, tooltipVisible && (React.createElement(motion.div, __assign({}, TOOLTIP_MOTION, { initial: { opacity: 0, x: tooltipPosition.x, y: tooltipPosition.y }, animate: {
                x: tooltipPosition.x,
                y: tooltipPosition.y,
                opacity: 1,
            }, style: {
                position: 'absolute',
                pointerEvents: 'none',
            } }),
            React.createElement(Tooltip, { mode: tooltipSettings.mode, hasArrow: false },
                React.createElement(Text, __assign({}, tooltipSettings.labels.typography), formatTooltip
                    ? valueFormatter(tooltipMeta.value, formatTooltip)
                    : tooltipMeta.value))))),
        React.createElement(ChartBase, { ref: svgElement, theme: theme, svgDimensions: svgDimensions, margins: margins },
            React.createElement("g", { style: {
                    transform: "translate(" + svgDimensions.width / 2 + "px, " + svgDimensions.height / 2 + "px)",
                } },
                React.createElement("g", { style: {
                        textAnchor: 'middle',
                        dominantBaseline: 'central',
                    } },
                    React.createElement("path", { d: outerArcPath, fill: gaugeSettings.border.backgroundColor }),
                    gaugeSettings.total.enabled && (React.createElement(AnimatePresence, null,
                        React.createElement(motion.g, __assign({}, progressValueMotion),
                            React.createElement(GaugeProgress, { progressType: progressType, formatValue: formatValue, isAutoApplied: minValue === 'auto' || maxValue === 'auto', typography: gaugeSettings.total.typography, minimum: minimum, maximum: maximum, value: progressValue }))))),
                React.createElement(AnimatePresence, null, innerArcs.map(function (_a, idx) {
                    var path = _a.path, value = _a.value, color = _a.color;
                    return (React.createElement(motion.path, __assign({ key: idx, "data-testid": "path-" + idx, d: path, fill: color }, createArcMotion(idx), { onMouseLeave: function () {
                            tooltipTimeout.current = setTimeout(function () {
                                hideTooltip();
                            }, TOOLTIP_TIMEOUT);
                        }, onMouseEnter: function (e) {
                            if (tooltipSettings.enabled) {
                                if (tooltipTimeout.current)
                                    clearTimeout(tooltipTimeout.current);
                                updateTooltipPosition(e, null, { value: value });
                            }
                        } })));
                })),
                gaugeSettings.labels.enabled && (React.createElement(GaugeLabels, { minValue: minValue, maxValue: maxValue, formatValue: formatValue, typography: gaugeSettings.labels.typography, arcPath: emptySpaceArcPath }))))));
};
export default GaugeChart;
